'use client'

import * as React from 'react'
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google'
import { continueWithGoogle } from '@/lib/growth/google-signup'
import { trackSignUpStart } from '@/lib/analytics/ga4'

// Same Google OAuth client as nextapp (utils/constants.ts googleClientId) —
// its "Authorized JavaScript origins" list must include idukki.io for this
// to work in production; that's a Google Cloud Console change, not code.
const GOOGLE_CLIENT_ID =
  process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID ||
  '1070239855738-75vsvq2i257n8n4p14r6m3vducb5oskf.apps.googleusercontent.com'

function GoogleGMark({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.95v2.33A9 9 0 0 0 9 18Z" />
      <path fill="#FBBC05" d="M3.97 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.95H.95A9 9 0 0 0 0 9c0 1.45.35 2.83.95 4.05l3.02-2.33Z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .95 4.95l3.02 2.33C4.68 5.16 6.66 3.58 9 3.58Z" />
    </svg>
  )
}

function GoogleButtonInner({ surface, onDone }: { surface: string; onDone: (result: { ok: boolean }) => void }) {
  const [state, setState] = React.useState<'idle' | 'loading' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = React.useState('')

  const login = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setState('loading')
      trackSignUpStart(surface)
      const result = await continueWithGoogle(tokenResponse.access_token)
      if (result.ok) {
        onDone({ ok: true })
        // Navigation to app.idukki.io is already in flight; leave the
        // button in its loading state rather than flashing an error.
        return
      }
      setState('error')
      setErrorMessage(result.message)
    },
    onError: () => {
      setState('error')
      setErrorMessage('Google sign-in was cancelled or blocked. Please try again.')
    },
  })

  return (
    <div className="flex flex-col gap-1.5">
      <button
        type="button"
        onClick={() => {
          setState('loading')
          login()
        }}
        disabled={state === 'loading'}
        className="inline-flex h-11 items-center justify-center gap-2.5 rounded-full border bg-background px-6 text-sm font-semibold text-foreground transition hover:-translate-y-0.5 hover:border-primary/40 disabled:pointer-events-none disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      >
        <GoogleGMark className="h-4 w-4 shrink-0" />
        {state === 'loading' ? 'Signing you in…' : 'Continue with Google'}
      </button>
      {state === 'error' && (
        <p className="text-center text-xs text-muted-foreground">{errorMessage}</p>
      )}
    </div>
  )
}

/**
 * "Continue with Google" — completes a real sign-up/login right from the
 * marketing site instead of only linking to app.idukki.io/register. Scoped
 * to high-intent growth surfaces (the exit-intent modal on compare pages);
 * mount it only when that surface is actually shown so the Google Identity
 * Services script isn't loaded on every page view.
 */
export function GoogleContinueButton({ surface, onDone }: { surface: string; onDone: (result: { ok: boolean }) => void }) {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <GoogleButtonInner surface={surface} onDone={onDone} />
    </GoogleOAuthProvider>
  )
}
